import { logger } from '@adornis/base/logging.js';
import type { Maybe, ValueEvent } from '@adornis/base/utilTypes.js';
import { XSnackbar } from '@adornis/chemistry/elements/components/x-snackbar.js';
import { stateful, useEffect, useState } from '@adornis/functional-lit/stateful.js';
import '@adornis/writer/x-writer.js';
import { hasPermission } from '@ils/permissions/hasPermission.js';
import { updateRatingNote } from '@ils/ratings/api/ratings.js';
import type { ExamSlotRatingDoc } from '@ils/ratings/db/rating.js';
import { html, nothing } from 'lit';
import { firstValueFrom, timer } from 'rxjs';
import type { ILSUser } from '../../db/ils-user.js';

export interface ILSOwnNotesProps {
  rating?: Maybe<ExamSlotRatingDoc>;
  currentUser?: Maybe<ILSUser>;
}

export const ILSOwnNotes = stateful<ILSOwnNotesProps>(({ rating, currentUser }) => {
  if (!hasPermission(currentUser, 'ratings', 'modifyownnotes') || !rating) return nothing;

  const [notes, setNotes] = useState(rating.notes ?? '');
  const [isSaving, setIsSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    setNotes(rating.notes ?? '');
  }, [rating._id, rating.notes]);

  useEffect(() => {
    const timeout = setTimeout(() => saveNotes(), 1000);
    return () => clearTimeout(timeout);
  }, [notes]);

  const saveNotes = async () => {
    try {
      setIsSaving(true);
      await Promise.all([updateRatingNote(rating._id, notes)(), firstValueFrom(timer(250))]);
      setSaved(true);
    } catch (e) {
      logger.error({ err: e }, 'Es gab ein Problem beim Speichern der Notizen');
      void XSnackbar.show('Es gab ein Problem beim Speichern der Notizen!');
    } finally {
      setIsSaving(false);
    }
  };

  return html`
    <x-flex space="xs">
      <x-writer
        placeholder="Notizen (${currentUser?.ecommerce?.fullName})"
        @value-changed=${(e: ValueEvent<string>) => {
          if (!e.detail.value || rating.notes === e.detail.value) return;
          setNotes(e.detail.value);
          setSaved(false);
          rating.notes = e.detail.value;
        }}
        .value=${notes}
      ></x-writer>
      <x-flex horizontal end space="xs">
        <x-button mode="text" icon="save" ?loading=${isSaving} @click=${saveNotes}> Speichern </x-button>
        ${saved ? html`<x-icon> check </x-icon>` : nothing}
      </x-flex>
    </x-flex>
  `;
});
